import type { State } from 'src/store/types.js';
import {
  currentUserUuid,
  employeeOptions,
  getClassificationText,
  getOrganization,
  isIssueProcessed,
  key,
  maritimeDoctor,
  toIssueViewModel,
} from 'src/store/selectors';
import { BASE_PATH } from '@trinnvis/dabih-api-client';
import type { ApiClient } from 'src/store/backend';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { IssueViewEditItem } from '../../content/issues/issue-view-edit-item';
import type { IssueView } from '../../content/issues/issue-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';

function employeeName(
  availableEmployees: { text: string; value: string }[],
  uuid: string | undefined,
  emptyValue: string = '',
) {
  return availableEmployees.find((e) => e.value === uuid)?.text ?? emptyValue;
}

function getWorkRelatedHealthIssueType(injury: boolean, sickness: boolean) {
  if (injury) {
    if (sickness) {
      return 'Skade og sykdom';
    }
    return 'Skade';
  }
  if (sickness) {
    return 'Sykdom';
  }
  return 'Ikke angitt';
}

export function buildIssueView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): IssueView {
  const organization = getOrganization(state);
  assertIsDefined(organization);
  const item = organization.issuesById[uuid];
  assertEntityFound(item, 'issues', uuid);
  const entity = toIssueViewModel(item);

  return {
    ...commonData,
    promoData: getPromoDataForEntity('issues', item, getFeatureStates(state)),
    /**
     * Report
     */
    reportedDate: entity.reportedDate ?? '',
    reportedBy: entity.reportedBy ?? '',
    reportedByName: employeeName(employeeOptions(state, entity.reportedBy), entity.reportedBy, 'Ukjent'),
    reportedTo: entity.reportedTo ?? '',
    reportedToName: employeeName(employeeOptions(state, entity.reportedTo), entity.reportedTo, 'Ukjent'),
    description: entity.description ?? '',
    eventDate: entity.eventDate ?? '',
    immediateMeasures: entity.immediateMeasures ?? '',
    furtherMeasures: entity.furtherMeasures ?? '',
    /**
     * Processing
     */
    name: entity.name ?? '',
    processedDate: entity.processedDate ?? '',
    processedBy: entity.processedBy ?? '',
    processedByOptions: employeeOptions(state, entity.processedBy),
    processedByName: employeeName(employeeOptions(state, entity.processedBy), entity.processedBy),
    notes: entity.notes ?? '',
    /**
     * Checklist
     */
    personDamagePossibility: entity.personDamagePossibility === true,
    relateToPatient: entity.relateToPatient === true,
    relateToEmployees: entity.relateToEmployees === true,
    workRelatedHealthIssue: entity.workRelatedHealthIssue === true,
    relateToEquipment: entity.relateToEquipment === true,
    relateToRadiation: entity.relateToRadiation === true,
    informationLeakagePossibility: entity.informationLeakagePossibility === true,
    relateToMaritimeHealthCertificate: entity.relateToMaritimeHealthCertificate === true,
    relateToArtificialIntelligence: entity.relateToArtificialIntelligence === true,
    /**
     * Work related health issue
     */
    workRelatedHealthIssueEmployee: entity.workRelatedHealthIssueEmployee ?? '',
    workRelatedHealthIssueEmployeeName: employeeName(
      employeeOptions(state, entity.workRelatedHealthIssueEmployee),
      entity.workRelatedHealthIssueEmployee,
    ),
    workRelatedHealthIssueEmployeeOptions: employeeOptions(state, entity.workRelatedHealthIssueEmployee, true),
    workRelatedHealthIssueDescription: entity.workRelatedHealthIssueDescription ?? '',
    workRelatedInjury: entity.workRelatedInjury === true,
    workRelatedSickness: entity.workRelatedSickness === true,
    workRelatedHealthIssueType: getWorkRelatedHealthIssueType(
      entity.workRelatedInjury === true,
      entity.workRelatedSickness === true,
    ),
    workRelatedInjuryTask: entity.workRelatedInjuryTask ?? '',
    workRelatedInjuryIncidentCause: entity.workRelatedInjuryIncidentCause ?? '',
    workRelatedSicknessCausingTask: entity.workRelatedSicknessCausingTask ?? '',
    workRelatedSicknessCausingTaskLocation: entity.workRelatedSicknessCausingTaskLocation ?? '',
    workRelatedSicknessCausingEnvironment: entity.workRelatedSicknessCausingEnvironment ?? '',
    /**
     * Measures
     */
    requiresMeasures: entity.requiresMeasures,
    identifiedMeasures: entity.identifiedMeasures ?? '',
    implementedMeasure: entity.implementedMeasure,
    measuresSatisfactorily: entity.measuresSatisfactorily,
    /**
     * Evaluation
     */
    evaluated: entity.evaluated, // I.e. 'needs evaluation'
    evaluatedDate: entity.evaluatedDate ?? '',
    evaluatedBy: entity.evaluatedBy ?? '',
    evaluatedByOptions: employeeOptions(state, entity.evaluatedBy),
    evaluatedByName: employeeName(employeeOptions(state, entity.evaluatedBy), entity.evaluatedBy),
    evaluatedOk: entity.evaluatedOk,
    processed: isIssueProcessed(entity),
    /**
     * Deadline (frist) – krav 1.2.1
     */
    deadline: entity.deadline ?? '',
    deadlineExtensions: (entity.deadlineExtensions ?? []).map((x) => ({
      previousDeadline: x.previousDeadline ?? '',
      newDeadline: x.newDeadline ?? '',
      reason: x.reason ?? '',
      changedByName: employeeName(employeeOptions(state, x.changedBy), x.changedBy, ''),
      changedAt: x.changedAt ?? '',
    })),
    /**
     * Meta data and ui resources
     */
    type: 'issues',
    uuid: entity.uuid ?? '',
    pages: (entity.pages ?? []).map((x) => '' + x),
    availablePages: pagesAsSelectTagOptions(state).map((p) => ({ ...p, disabled: p.value === '281' })),
    classification: entity.classification ?? 'NONE',
    accessControl: entity.accessControl ?? [],
    classificationText: getClassificationText(
      entity.classification ?? 'NONE',
      entity.accessControl ?? [],
      commonData.accessControlOptions,
    ),
    maritimeDoctor: maritimeDoctor(state),
    isConfirmedEntity: entity.isConfirmedEntity,
    attachments: toAttachmentItems(api, 'issues', entity.uuid, entity.attachments),
    deleted: entity.deleted === true,
    deletable: true,
    parentHref: currentParent,
    href: currentParent + '/issues/' + uuid,
    pdfHref: BASE_PATH + '/organizations/' + organization.organizationId + '/issues/' + uuid + '.pdf?key=' + key(state),
    helpContent: '',
    currentUserHasAccess:
      entity.classification !== 'CONFIDENTIAL' || (entity.accessControl ?? []).includes(currentUserUuid(state) ?? ''),
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'issues', uuid, organization.organizationId);
      return command.draft as IssueViewEditItem;
    },
    docsForLinking: [],
  };
}
